import React from 'react';
import { OfferDetailsData } from '@/pages/hettwer/types/OfferDetailsData';

interface OfferHeaderProps {
  offer: OfferDetailsData;
  onChange: (offer: OfferDetailsData) => void;
}

/**
 * OfferHeader
 *
 * Header/editor block for general offer metadata.
 * - Upper row: identity & dates
 * - Lower row: status & context
 * - Dropdowns are color-coded for quick visual feedback
 */
export const OfferHeader: React.FC<OfferHeaderProps> = ({ offer, onChange }) => {
  const update = (patch: Partial<OfferDetailsData>) => onChange({ ...offer, ...patch });

  const renderInput = (
    label: string,
    value: string | null | undefined,
    onChangeValue?: (value: string) => void,
    options?: { type?: string }
  ) => (
    <div className="flex flex-col gap-1">
      <label className="text-xs text-gray-600">{label}</label>
      <input
        type={options?.type ?? 'text'}
        className="input input-sm w-full"
        value={value ?? ''}
        onChange={(e) => onChangeValue?.(e.target.value)}
      />
    </div>
  );

  const renderSelect = (
    label: string,
    value: string,
    options: { value: string; label: string }[],
    onChangeValue: (value: string) => void,
    selectClass?: string
  ) => (
    <div className="flex flex-col gap-1">
      <label className="text-xs text-gray-600">{label}</label>
      <select
        className={`select select-sm w-full ${selectClass ?? ''}`}
        value={value}
        onChange={(e) => onChangeValue(e.target.value)}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );

  /* ==================================================
     Color helpers (backend-consistent)
     ================================================== */

  const statusClass =
    offer.status === 'final'
      ? 'bg-green-50 text-green-800 border-green-300'
      : 'bg-yellow-50 text-yellow-800 border-yellow-300';

  // @ts-ignore
  const isTraining = !!offer.training?.is_training_data;

  const trainingClass = isTraining
    ? 'bg-blue-50 text-blue-800 border-blue-300'
    : 'bg-gray-50 text-gray-700 border-gray-300';

  return (
    <div className="card">
      <div className="card-header">
        <h3 className="card-title">Angebot</h3>
      </div>

      <div className="card-body space-y-6">
        {/* ==================================================
           Upper row – identity & dates
           ================================================== */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
          {renderInput('Angebotsnummer', offer.offer_number, (v) => update({ offer_number: v }))}

          {renderSelect(
            'Dokumenttyp',
            offer.document_type,
            [
              { value: 'angebot', label: 'Angebot' },
              { value: 'rechnung', label: 'Rechnung' }
            ],
            (v) => update({ document_type: v })
          )}

          {renderInput('Angebotsdatum', offer.offer_date, (v) => update({ offer_date: v }), {
            type: 'date'
          })}

          {renderInput('Gültig bis', offer.valid_until, (v) => update({ valid_until: v }), {
            type: 'date'
          })}
        </div>

        {/* ==================================================
           Lower row – status & training
           ================================================== */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {renderSelect(
            'Status',
            offer.status,
            [
              { value: 'draft', label: 'Entwurf' },
              { value: 'final', label: 'Fertiges Dokument' }
            ],
            (v) => update({ status: v }),
            statusClass
          )}

          {renderSelect(
            'Trainingsdokument',
            isTraining ? 'yes' : 'no',
            [
              { value: 'no', label: 'Nein' },
              { value: 'yes', label: 'Ja' }
            ],
            (v) =>
              update({
                training:
                  v === 'yes'
                    ? {
                        is_training_data: true,
                        approved_for_training: false
                      }
                    : null
              }),
            trainingClass
          )}
        </div>
      </div>
    </div>
  );
};
